<template>
  <div class="relative" ref="dropdown">
    <!-- Trigger button -->
    <button @click="toggleDropdown"
      class="flex items-center px-3 py-2 bg-white dark:bg-stone-700 border border-stone-300 dark:border-stone-600 rounded-md shadow-sm hover:bg-stone-50 dark:hover:bg-stone-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors">
      <img :src="userPhotoUrl" class="w-7 h-7 rounded-full mr-2" alt="User avatar">
      <i class="fas fa-caret-down text-stone-500 dark:text-stone-400"></i>
    </button>

    <!-- Dropdown menu -->
    <div v-if="isOpen"
      class="absolute right-0 mt-2 w-64 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-md shadow-lg z-50">
      <!-- Signed in as (disabled item) -->
      <div class="px-4 py-2 text-sm text-stone-500 dark:text-stone-400 border-b border-stone-200 dark:border-stone-700">
        {{ t('profiledropdown.Signed in as', { name: displayName }) }}
      </div>

      <!-- Sign out -->
      <button @click="logOut"
        class="w-full text-left px-4 py-2 text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors">
        {{ t('profiledropdown.Sign out') }}
      </button>

      <!-- Export data -->
      <button @click="$router.push('/export')"
        class="w-full text-left px-4 py-2 text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors">
        {{ t('profiledropdown.Export my data') }}
      </button>

      <!-- Delete account -->
      <button @click="confirmAndDelete"
        class="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors border-t border-stone-200 dark:border-stone-700">
        {{ t('profiledropdown.Delete my account') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import { useUserStore } from '@/stores/useUserStore';
import { useWheelStore } from '@/stores/useWheelStore';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const userStore = useUserStore();
const wheelStore = useWheelStore();
const emit = defineEmits(['log-out', 'start-wait-animation', 'show-snackbar-message', 'stop-wait-animation']);

const isOpen = ref(false);
const dropdown = ref(null);

const userPhotoUrl = computed(() => userStore.getUserPhotoUrl || '/images/user_profile.png');
const displayName = computed(() => userStore.getDisplayName || '');

function toggleDropdown() {
  isOpen.value = !isOpen.value;
}

function handleClickOutside(event) {
  if (dropdown.value && !dropdown.value.contains(event.target)) {
    isOpen.value = false;
  }
}

function logOut() {
  isOpen.value = false;
  userStore.logOut();
  emit('show-snackbar-message', t('profiledropdown.Signed out'));
  emit('log-out');
}

function confirmAndDelete() {
  isOpen.value = false;
  window.$ui.dialog.confirm({
    title: t('profiledropdown.Delete account'),
    message: t('profiledropdown.Are you sure you want to delete your account'),
    cancelText: t('common.Cancel'),
    confirmText: t('common.Delete'),
    type: 'is-danger',
    hasIcon: true,
    onConfirm: () => deleteAccount()
  });
}

async function deleteAccount() {
  emit('start-wait-animation');
  await userStore.deleteAccount();
  wheelStore.resetWheel();
  const msg = t('profiledropdown.Your account and your saved wheels have been deleted');
  emit('show-snackbar-message', msg);
  emit('stop-wait-animation');
  emit('log-out');
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>
